<template>
  <customDialog
    v-model="show"
    title="reconciliation"
    width="760px"
    :footer="false"
    :closeBtn="true"
  >
    <template #activator>
      <div v-if="shift" class="reconciliation-dialog">
        <div class="recon-info-box">
          <div class="recon-info-item">
            <span class="recon-info-label">{{ $t("staff_name") }}</span>
            <span class="recon-info-value">{{ shift.user ? shift.user.first_name : "-" }}</span>
          </div>
          <div class="recon-info-item recon-info-item--full">
            <span class="recon-info-label">{{ $t("shift_period") }}</span>
            <span class="recon-info-value">
              {{ shift.clock_in_at | moment("DD/MM/YYYY HH:mm") }} -
              {{ shift.clock_out_at | moment("DD/MM/YYYY HH:mm") }}
            </span>
          </div>
        </div>

        <div class="recon-stats-grid">
          <div class="recon-stat">
            <div class="recon-stat-icon">
              <v-icon color="#438ef2" size="18">fal fa-wallet</v-icon>
            </div>
            <div class="recon-stat-label">{{ $t("opening_cash") }}</div>
            <div class="recon-stat-value">{{ shift.opening_cash | NumberFormat }}</div>
          </div>
          <div class="recon-stat">
            <div class="recon-stat-icon">
              <v-icon color="#438ef2" size="18">fal fa-chart-line</v-icon>
            </div>
            <div class="recon-stat-label">{{ $t("total_sales") }}</div>
            <div class="recon-stat-value">{{ (shift.total_sales || 0) | NumberFormat }}</div>
          </div>
          <div class="recon-stat">
            <div class="recon-stat-icon recon-stat-icon--green">
              <v-icon color="#15803d" size="18">fal fa-arrow-down</v-icon>
            </div>
            <div class="recon-stat-label">{{ $t("cash_in") }}</div>
            <div class="recon-stat-value recon-stat-value--positive">
              {{ (shift.cash_in_total || 0) | NumberFormat }}
            </div>
          </div>
          <div class="recon-stat">
            <div class="recon-stat-icon recon-stat-icon--red">
              <v-icon color="#dc2626" size="18">fal fa-arrow-up</v-icon>
            </div>
            <div class="recon-stat-label">{{ $t("cash_out") }}</div>
            <div class="recon-stat-value recon-stat-value--negative">
              {{ (shift.cash_out_total || 0) | NumberFormat }}
            </div>
          </div>
        </div>

        <div
          class="recon-compare-box"
          :class="calculatedVariance >= 0 ? 'recon-compare-box--positive' : 'recon-compare-box--negative'"
        >
          <div class="recon-compare-col">
            <div class="recon-compare-label">{{ $t("expected_cash") }}</div>
            <div class="recon-compare-value">{{ calculatedExpected | NumberFormat }}</div>
          </div>
          <v-icon :color="calculatedVariance >= 0 ? '#438ef2' : '#dc2626'" size="22">
            fal fa-arrow-right
          </v-icon>
          <div class="recon-compare-col">
            <div class="recon-compare-label">{{ $t("actual_cash") }}</div>
            <div class="recon-compare-value">{{ form.actual_cash | NumberFormat }}</div>
          </div>
        </div>

        <v-form ref="form" class="recon-form">
          <CurrencyInput
            label="actual_cash"
            v-model="form.actual_cash"
            :error-messages="actualCashErrors"
            @input="
              $v.form.actual_cash.$touch();
              calculateVariance();
            "
            outlined
            dense
            :hint="$t('enter_counted_cash_amount')"
            persistent-hint
          />

          <TextArea
            id="reconciliation-notes"
            label="notes"
            :aria-label="$t('notes')"
            v-model="form.notes"
            outlined
            :rows="3"
            placeholder="reconciliation_notes_placeholder"
            dense
            class="recon-notes-field"
            :errorMessages="[]"
          />
        </v-form>

        <div
          v-if="form.actual_cash > 0"
          class="recon-variance-chip"
          :class="calculatedVariance >= 0 ? 'recon-variance-chip--positive' : 'recon-variance-chip--negative'"
        >
          <v-icon size="14" :color="calculatedVariance >= 0 ? '#15803d' : '#dc2626'" class="mr-2">
            {{ calculatedVariance >= 0 ? "fal fa-check-circle" : "fal fa-exclamation-circle" }}
          </v-icon>
          {{ $t("variance") }}: {{ calculatedVariance | NumberFormat }}
        </div>

        <div class="recon-actions">
          <v-btn
            depressed
            color="#DDEAFD"
            class="text-none primary--text rounded-pill recon-action-btn"
            @click="close"
          >
            {{ $t("cancel") }}
          </v-btn>
          <v-spacer />
          <v-btn
            depressed
            color="primary"
            class="text-none rounded-pill recon-action-btn recon-action-btn-primary"
            :loading="loading"
            :disabled="$v.form.$invalid"
            @click="submit"
          >
            <v-icon left small>fal fa-save</v-icon>
            {{ $t("save_reconciliation") }}
          </v-btn>
        </div>
      </div>
    </template>
  </customDialog>
</template>

<script>
import { mapActions } from "vuex";
import CurrencyInput from "@/components/CurrencyInput";
import TextArea from "@/components/global/TextArea";
import customDialog from "@/components/customDialog";
import { required, minValue } from "vuelidate/lib/validators";
import { success } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";

export default {
  name: "ReconciliationDialog",
  components: { CurrencyInput, TextArea, customDialog },
  mixins: [myMixin],
  props: {
    value: Boolean,
    shift: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    form: {
      actual_cash: 0,
      notes: "",
    },
    loading: false,
    calculatedVariance: 0,
  }),
  validations: {
    form: {
      actual_cash: { required, minValue: minValue(0) },
    },
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    actualCashErrors() {
      if (!this.$v.form.actual_cash.$dirty) return [];
      return [
        !this.$v.form.actual_cash.required && this.$t("this_field_is_required"),
        !this.$v.form.actual_cash.minValue && this.$t("value_must_be_positive"),
      ].filter(Boolean);
    },
    calculatedExpected() {
      if (!this.shift) return 0;
      const opening = Number.parseFloat(this.shift.opening_cash) || 0;
      const sales = Number.parseFloat(this.shift.total_sales) || 0;
      const cashIn = Number.parseFloat(this.shift.cash_in_total) || 0;
      const cashOut = Number.parseFloat(this.shift.cash_out_total) || 0;
      return opening + sales + cashIn - cashOut;
    },
  },
  methods: {
    ...mapActions({ createReconciliation: "index/createReconciliation" }),
    close() {
      this.show = false;
      this.$v.form.$reset();
      this.form = { actual_cash: 0, notes: "" };
      this.calculatedVariance = 0;
    },
    calculateVariance() {
      if (!this.form.actual_cash) {
        this.calculatedVariance = 0;
        return;
      }
      const actualCash = Number.parseFloat(this.form.actual_cash) || 0;
      const expectedCash = this.calculatedExpected || 0;
      this.calculatedVariance = actualCash - expectedCash;
    },
    async submit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;

      if (!this.shift || !this.shift.id) {
        this.$toast.error(this.$t("no_shift_selected"));
        return;
      }

      try {
        this.loading = true;
        await this.createReconciliation({
          that: this,
          shiftId: this.shift.id,
          expectedCash: this.calculatedExpected,
          actualCash: this.form.actual_cash,
          notes: this.form.notes,
        });
        await success(this, "reconciliation_saved");
        this.$emit("success");
        this.close();
      } catch (error) {
        this.$toast.error(
          error.response?.data?.message || this.$t("error_occurred")
        );
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    show(val) {
      if (val && this.shift) {
        // Pre-fill with closing cash if available
        if (this.shift.closing_cash) {
          this.form.actual_cash = Number.parseFloat(this.shift.closing_cash) || 0;
          this.calculateVariance();
        }
      }
    },
  },
};
</script>

<style scoped>
.reconciliation-dialog {
  padding: 2px 2px 4px;
}

.recon-info-box {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 20px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 14px;
  margin-bottom: 18px;
}

.recon-info-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.recon-info-item--full {
  grid-column: 1 / -1;
}

.recon-info-label {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.recon-info-value {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.recon-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}

.recon-stat {
  padding: 14px 12px;
  border: 1px solid #e8eef6;
  border-radius: 14px;
  background: #fff;
  text-align: center;
}

.recon-stat-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #eaf3ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 8px;
}

.recon-stat-icon--green {
  background: #e7f8ee;
}

.recon-stat-icon--red {
  background: #fdecec;
}

.recon-stat-label {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  margin-bottom: 2px;
}

.recon-stat-value {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.recon-stat-value--positive {
  color: #15803d;
}

.recon-stat-value--negative {
  color: #dc2626;
}

.recon-compare-box {
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 18px 16px;
  border-radius: 16px;
  background: #eaf3ff;
  margin-bottom: 20px;
}

.recon-compare-box--negative {
  background: #fdecec;
}

.recon-compare-col {
  text-align: center;
}

.recon-compare-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
}

.recon-compare-value {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
}

.section-label {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
}

.recon-notes-field {
  margin-top: 16px;
}

.recon-form ::v-deep .v-text-field--outlined .v-input__slot {
  background: #ffffff !important;
  border-radius: 10px !important;
  min-height: 46px;
}

.recon-form ::v-deep .v-text-field--outlined fieldset {
  border-color: #cbd5e1 !important;
  border-width: 1.5px !important;
  transition: border-color 0.2s ease;
}

.recon-form ::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #438ef2 !important;
  border-width: 2px !important;
}


.recon-variance-chip {
  display: inline-flex;
  align-items: center;
  margin-top: 16px;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
}

.recon-variance-chip--positive {
  background: #e7f8ee;
  color: #15803d;
}

.recon-variance-chip--negative {
  background: #fdecec;
  color: #b91c1c;
}

.recon-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
}

.recon-action-btn {
  height: 44px !important;
  padding: 0 22px !important;
  font-weight: 700;
  box-shadow: none !important;
  letter-spacing: normal;
}

.recon-action-btn-primary {
  height: 48px !important;
  padding: 0 28px !important;
}

@media (max-width: 600px) {
  .recon-stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .recon-compare-box {
    flex-direction: column;
    gap: 12px;
  }
}

.theme--dark .recon-info-box {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .recon-info-label,
.theme--dark .recon-stat-label,
.theme--dark .recon-compare-label {
  color: rgba(255, 255, 255, 0.5);
}

.theme--dark .recon-info-value,
.theme--dark .recon-stat-value,
.theme--dark .recon-compare-value,
.theme--dark .section-label {
  color: rgba(255, 255, 255, 0.87);
}

.theme--dark .recon-stat {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .recon-stat-icon {
  background: rgba(66, 165, 245, 0.16);
}

.theme--dark .recon-stat-icon--green {
  background: rgba(76, 175, 80, 0.16);
}

.theme--dark .recon-stat-icon--red {
  background: rgba(244, 67, 54, 0.16);
}

.theme--dark .recon-compare-box {
  background: rgba(66, 165, 245, 0.12);
}

.theme--dark .recon-compare-box--negative {
  background: rgba(244, 67, 54, 0.12);
}

.theme--dark .recon-form ::v-deep .v-text-field--outlined .v-input__slot {
  background: var(--app-input-bg, #1e293b) !important;
}

.theme--dark .recon-form ::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(255, 255, 255, 0.24) !important;
}

.theme--dark .recon-variance-chip--positive {
  background: rgba(76, 175, 80, 0.16);
  color: #81c784;
}

.theme--dark .recon-variance-chip--negative {
  background: rgba(244, 67, 54, 0.16);
  color: #e57373;
}
</style>
